import type {Hours} from "./PracticesMapContainer.tsx";
import {useState} from "react";
import {ChevronDown, ChevronUp, Clock} from "lucide-react";

const BusinessHoursDisplay = ({regularBusinessHours}: { regularBusinessHours: Hours }) => {
    const [isExpanded, setIsExpanded] = useState(false);


    const formatTime = (hour: number, minute: number) => {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const displayMinute = minute.toString().padStart(2, '0');
        return `${displayHour}:${displayMinute} ${period}`;
    };

    const getCurrentDayIndex = () => {
        return new Date().getDay();
    };

    const getTodayHours = () => {
        const today = getCurrentDayIndex();
        const hours = regularBusinessHours;

        if (!hours || !hours.periods) return null;

        const todayPeriod = hours.periods.find(period => period.open.day === today);

        if (!todayPeriod) return 'Closed';

        return `${formatTime(todayPeriod.open.hour, todayPeriod.open.minute)} - ${formatTime(todayPeriod.close.hour, todayPeriod.close.minute)}`;
    };

    const getOpenStatus = () => {
        const hours = regularBusinessHours;
        if (!hours) return {text: 'Hours unknown', color: 'text-gray-600'};

        return hours.openNow
            ? {text: 'Open now', color: 'text-white'}
            : {text: 'Closed', color: 'text-red-800'};
    };

    const hours = regularBusinessHours;
    const status = getOpenStatus();
    const todayHours = getTodayHours();
    const currentDay = getCurrentDayIndex();

    return (
        <div className="max-w-md mx-auto">

            {hours && (
                <div className="bg-green-600 p-4 mt-4 pt-4 rounded-lg">
                    <div className="flex items-start gap-2">
                        <Clock className="w-5 h-5 mt-0.5 flex-shrink-0 text-white"/>
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                                <span className={`font-semibold ${status.color}`}>
                                  {status.text}
                                </span>
                            </div>
                            {todayHours && (
                                <div className="text-sm text-white mb-2">
                                    {todayHours}
                                </div>
                            )}
                            {/* Toggle for all hours */}
                            <button
                                onClick={() => {
                                    if (!isExpanded) {
                                        window.dataLayer.push({
                                            event: 'feature',
                                            feature_name: 'View Practice Business Hours'
                                        })
                                    }

                                    setIsExpanded(!isExpanded)
                                }}
                                className="flex items-center gap-1 text-sm text-white opacity-65 hover:opacity-80 focus:outline-none"
                            >
                                {isExpanded ? (
                                    <>
                                        <span>Hide hours</span>
                                        <ChevronUp className="w-4 h-4"/>
                                    </>
                                ) : (
                                    <>
                                        <span>Show all hours</span>
                                        <ChevronDown className="w-4 h-4"/>
                                    </>
                                )}
                            </button>

                            {/* Expanded hours list */}
                            {isExpanded && hours.weekdayDescriptions && (
                                <div className="mt-3 space-y-1">
                                    {hours.weekdayDescriptions.map((desc, index) => {
                                        // weekdayDescriptions starts with Monday (index 0)
                                        // Convert to our day numbering (0=Sunday, 1=Monday, etc)
                                        const dayIndex = index === 6 ? 0 : index + 1;
                                        const isToday = dayIndex === currentDay;

                                        return (
                                            <div
                                                key={index}
                                                className={`text-sm py-1 ${
                                                    isToday
                                                        ? 'font-semibold text-white'
                                                        : 'text-white opacity-80'
                                                }`}
                                            >
                                                {desc}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BusinessHoursDisplay;